import { fromJS, List, Record } from 'immutable';
import type { ActionType } from 'typesafe-actions';
import { logoutUser } from '../auth/actions.js';
import { IMPORT_STATUS } from '../common/types.js';
import handleAction from '../common/utils/handle-action.js';
import type { ImmutableMap } from '../common/utils/immutable-types.js';
import simpleReducer from '../common/utils/simple-reducer.js';
import {
  CREATE_PROJECT,
  DELETE_PROJECT,
  GET_PROJECT,
  GET_PROJECT_LIST,
  GET_PROJECT_MEMBERS,
  SET_CURRENT_PROJECT,
  SET_IMPORT_STATUS
} from './action-types';
import { getProjectAdmins, type setCurrentProject, type setImportStatus, updateProjectSettings } from './actions.js';
import type { Member, Project, ProjectListItem, ProjectState as ProjectStateType } from './types.js';

const emptyList = List();

export const ProjectState = Record({
  error: null,

  loading: false,
  loaded: false,
  data: null,

  list: null,
  listLoading: false,
  listLoaded: false,
  listError: false,

  members: null,
  membersError: null,
  membersLoading: false,
  membersLoaded: true,
  admins: emptyList,

  adding: false,
  added: false,

  removing: false,
  removed: false,

  updating: false,

  importStatus: IMPORT_STATUS.NOT_STARTED,

  currentId: '',

  settingsSchemaVersion: 0
});

export const initialState = new ProjectState();

const getProjectListHandlers = {
  PENDING(state: ImmutableMap<ProjectStateType>) {
    return state.merge({
      listLoading: true,
      listError: null
    });
  },
  FULFILLED(state: ImmutableMap<ProjectStateType>, { payload }: { payload: { projects: ProjectListItem[] | null } }) {
    return state.merge({
      list: fromJS(payload.projects),
      listError: null,
      listLoading: false,
      listLoaded: true
    });
  },
  REJECTED(state: ImmutableMap<ProjectStateType>, { payload }: { payload: { message: string } }) {
    return state.merge({
      listError: payload.message,
      listLoading: false
    });
  }
};

const getProjectMembersHandlers = {
  PENDING(state: ImmutableMap<ProjectStateType>) {
    return state.merge({
      membersLoading: true,
      membersError: null
    });
  },
  FULFILLED(state: ImmutableMap<ProjectStateType>, { payload }: { payload: { members: Member[] | null } }) {
    return state.merge({
      members: fromJS(payload.members),
      membersError: null,
      membersLoading: false,
      membersLoaded: true
    });
  },
  REJECTED(state: ImmutableMap<ProjectStateType>, { payload }: { payload: { message: string } }) {
    return state.merge({
      membersError: payload.message,
      membersLoading: false
    });
  }
};

const getProjectHandlers = {
  PENDING(state: ImmutableMap<ProjectStateType>) {
    return state.merge({
      loading: true,
      loaded: false,
      error: null
    });
  },
  FULFILLED(state: ImmutableMap<ProjectStateType>, { payload }: { payload: { project: Project } }) {
    if (!payload) {
      return state.merge({
        error: 'Project not found.',
        loading: false
      });
    }

    const { project } = payload;
    return state.merge({
      data: fromJS(project),
      error: null,
      loading: false,
      loaded: true
    });
  },
  REJECTED(state: ImmutableMap<ProjectStateType>, { payload }: { payload: { message: string } }) {
    return state.merge({
      error: payload.message,
      loading: false
    });
  }
};

const addProjectHandlers = {
  PENDING(state: ImmutableMap<ProjectStateType>) {
    return state.merge({
      adding: true,
      added: false,
      error: null
    });
  },
  FULFILLED(state: ImmutableMap<ProjectStateType>) {
    return state.merge({
      error: null,
      adding: false,
      added: true
    });
  },
  REJECTED(state: ImmutableMap<ProjectStateType>, { payload }: { payload: { message: string } }) {
    return state.merge({
      error: payload.message,
      adding: false
    });
  }
};

const removeProjectHandlers = {
  PENDING(state: ImmutableMap<ProjectStateType>) {
    return state.merge({
      error: null,
      removing: true,
      removed: false
    });
  },
  FULFILLED(state: ImmutableMap<ProjectStateType>) {
    return state.merge({
      error: null,
      removing: false,
      removed: true
    });
  },
  REJECTED(state: ImmutableMap<ProjectStateType>, { payload }: { payload: { message: string } }) {
    return state.merge({
      error: payload.message,
      removing: false
    });
  }
};

const updateProjectSettingsHandlers = {
  PENDING(state: ImmutableMap<ProjectStateType>) {
    return state.set('updating', true);
  },
  FULFILLED(state: ImmutableMap<ProjectStateType>, { payload }: ActionType<typeof updateProjectSettings.fulfilled>) {
    return state.mergeDeep({
      updating: false,
      data: {
        name: payload.updateProject.name,
        avatar: payload.updateProject.avatar,
        defaultTimezone: payload.updateProject.defaultTimezone
      },
      // TODO somewhere this value is getting set right on the immutable map object
      // @ts-expect-error

      settingsSchemaVersion: state.settingsSchemaVersion + 1
    });
  },
  REJECTED(state: ImmutableMap<ProjectStateType>, { payload }: ActionType<typeof updateProjectSettings.rejected>) {
    return state.merge({
      error: payload.message,
      updating: false
    });
  }
};

export default simpleReducer(initialState, {
  ...handleAction(GET_PROJECT_LIST, getProjectListHandlers),
  ...handleAction(GET_PROJECT_MEMBERS, getProjectMembersHandlers),
  ...handleAction(GET_PROJECT, getProjectHandlers),
  ...handleAction(CREATE_PROJECT, addProjectHandlers),
  ...handleAction(DELETE_PROJECT, removeProjectHandlers),
  ...handleAction(updateProjectSettings.PREFIX, updateProjectSettingsHandlers),
  [SET_CURRENT_PROJECT]: (state: ImmutableMap<ProjectStateType>, { payload }: ActionType<typeof setCurrentProject>) =>
    state.set('currentId', payload.projectId),
  [logoutUser.FULFILLED]: () => initialState,
  [getProjectAdmins.FULFILLED](
    state: ImmutableMap<ProjectStateType>,
    { payload }: ActionType<typeof getProjectAdmins.fulfilled>
  ) {
    return state.set('admins', fromJS(payload.admins));
  },
  [SET_IMPORT_STATUS]: (state: ImmutableMap<ProjectStateType>, { payload }: ActionType<typeof setImportStatus>) =>
    state.set('importStatus', payload)
});
